import React, { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTable, DataTableRow, Skeleton, colors } from '@fs/zion-ui'
import { css } from '@emotion/core'
import SourceCellSkeleton from './SourceCellSkeleton'
import SourceColumnHeaderSkeleton from './SourceColumnHeaderSkeleton'
import SourceGridToolbarSkeleton from './SourceGridToolbarSkeleton'
import { SOURCE_COLUMN_WIDTH } from './sourceGridConstants'

// 5 placeholder source rows
const SOURCE_ROW_IDS = ['source-1', 'source-2', 'source-3', 'source-4', 'source-5'] as const

// Vital columns + residence + relationship columns — mirrors the live DataTable column set
const DATA_COL_IDS = [
  'col-name',
  'col-gender',
  'col-birth',
  'col-christening',
  'col-death',
  'col-burial',
  'col-residence',
  'col-parents',
  'col-spouses',
  'col-children',
] as const

const containerCss = css`
  border: 1px solid ${colors.gray10};
  border-radius: 4px;
  margin-top: var(--spacer-sm);
  overflow: hidden;
  td,
  th {
    border-inline-end: 1px solid ${colors.gray10};
    &:last-child {
      border-inline-end: none;
    }
  }
  tr > [sticky-col][last-sticky-col] {
    border-inline-end-color: ${colors.gray10};
  }
  tbody td {
    min-height: 80px;
  }
`

function makeRowCells(): Record<string, ReactNode> {
  const cells: Record<string, ReactNode> = { '0': <SourceColumnHeaderSkeleton /> }
  DATA_COL_IDS.forEach((id, i) => {
    cells[String(i + 1)] = <SourceCellSkeleton key={id} />
  })
  return cells
}

export default function SourceGridSkeleton(): JSX.Element {
  const [t] = useTranslation()
  const columns = [
    { label: <Skeleton.H5 aria-hidden />, fixedWidth: SOURCE_COLUMN_WIDTH },
    ...DATA_COL_IDS.map((id) => ({
      label: <Skeleton.H5 key={id} aria-hidden />,
      size: 'lg' as const,
    })),
  ]

  return (
    <>
      <SourceGridToolbarSkeleton />
      <div css={containerCss} aria-busy aria-label={t('tree-person.source-grid.loading-label')}>
        <DataTable columns={columns} stickyFirstColumn={1} verticalAlign="top" gutters="sm">
          {SOURCE_ROW_IDS.map((id) => (
            <DataTableRow key={id} {...(makeRowCells() as Record<string, ReactNode>)} />
          ))}
        </DataTable>
      </div>
    </>
  )
}
